import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { FlowArrowIcon } from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import type { WorkbookAutomation } from '@erp/rpc';
import { workbookParameters } from '@erp/workbooks';
import { orpc } from '@/api/rpc/client';
import { Badge } from '@/components/ui/badge';
import type { AutomationsSectionProps } from './workbookIntegrations';

/** Workflows' Automations section: what runs this Workbook or reacts to its Runs, and its Parameters. */
export function WorkbookWorkflowsSection({ editor }: AutomationsSectionProps) {
  const automations = useQuery(
    orpc.workbooks.automations.queryOptions({
      input: { workbookId: editor.workbookId },
    }),
  );
  const parameters = workbookParameters(editor.definition);
  const workflows = automations.data?.workflows ?? [];
  return (
    <section
      aria-labelledby="workbook-automations-workflows"
      className="space-y-2 rounded-lg border p-3"
    >
      <h3
        id="workbook-automations-workflows"
        className="flex items-center gap-2 text-sm font-medium"
      >
        <FlowArrowIcon className="size-4 text-muted-foreground" />
        <T context="workbook automations workflows section">Workflows</T>
      </h3>
      <p className="text-sm text-muted-foreground">
        <T context="workbook automations workflows help">
          Run this workbook from a workflow with the Run Workbook node, or start
          a workflow when one of your runs completes with the Workbook Run
          Completed event trigger.
        </T>
      </p>
      {automations.isPending ? (
        <p className="text-sm text-muted-foreground">
          <T context="workbook automations loading">Loading…</T>
        </p>
      ) : automations.error ? (
        <p role="alert" className="text-sm text-destructive">
          {automations.error.message}
        </p>
      ) : workflows.length ? (
        <ul className="divide-y rounded-md border">
          {workflows.map((workflow) => (
            <WorkflowRow key={workflow.workflowId} workflow={workflow} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          <T context="workbook automations no workflows">
            No workflows use this workbook yet.
          </T>
        </p>
      )}
      <div className="space-y-1">
        <h4 className="text-xs font-medium text-muted-foreground">
          <T context="workbook automations parameters heading">Parameters</T>
        </h4>
        {parameters.length ? (
          <ul className="space-y-1 text-sm">
            {parameters.map((parameter) => (
              <li key={parameter.sourceId}>
                <span className="font-medium">{parameter.name}</span>
                <span className="text-muted-foreground">
                  {' · '}
                  {parameter.columns.join(', ')}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            <T context="workbook automations no parameters">
              Add an inline table to give a workflow something to pass in. A
              workflow run can replace an inline table&apos;s rows for that run
              only.
            </T>
          </p>
        )}
      </div>
    </section>
  );
}

function WorkflowRow({ workflow }: { workflow: WorkbookAutomation }) {
  const gt = useGT();
  const relation =
    workflow.relation === 'runs'
      ? gt('Runs this workbook', { context: 'workbook automation relation' })
      : workflow.anyWorkbook
        ? gt('Starts when any workbook run completes', {
            context: 'workbook automation relation',
          })
        : gt('Starts when a run completes', {
            context: 'workbook automation relation',
          });
  return (
    <li className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-sm">
      <Link
        to="/workflows/$workflowId"
        params={{ workflowId: workflow.workflowId }}
        className="font-medium underline-offset-2 hover:underline"
      >
        {workflow.workflowName}
      </Link>
      <span className="text-muted-foreground">
        <T context="workbook automation version">
          v<Var>{workflow.version}</Var>
        </T>
      </span>
      <Badge variant="outline">{relation}</Badge>
      {!workflow.isActive && (
        <Badge variant="secondary">
          <T context="workbook automation inactive workflow">Inactive</T>
        </Badge>
      )}
    </li>
  );
}
